import React, { useState, useEffect } from 'react';
import { Table, Card, Typography, Button, Modal, Form, Select, DatePicker, Input, message, Popconfirm, Tag, Space } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { getAbsences, createAbsence, updateAbsence, deleteAbsence, getAdminTeams } from '../../services/adminService';
import dayjs from 'dayjs';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const AbsencePage = () => {
  const [absences, setAbsences] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAbsence, setEditingAbsence] = useState(null);
  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [absData, teamData] = await Promise.all([getAbsences({}), getAdminTeams()]);
      setAbsences(absData.absences || []);
      setTeams(teamData.teams || []);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleAdd = () => {
    setEditingAbsence(null);
    form.resetFields();
    setModalOpen(true);
  };

  const handleEdit = (absence) => {
    setEditingAbsence(absence);
    form.setFieldsValue({
      equipe_id: absence.equipe_id,
      dates: [dayjs(absence.date_debut), dayjs(absence.date_fin)],
      motif: absence.motif,
    });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteAbsence(id);
      message.success('Absence supprimée');
      fetchData();
    } catch (err) {
      message.error('Erreur');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const data = {
        equipe_id: values.equipe_id,
        date_debut: values.dates[0].format('YYYY-MM-DD'),
        date_fin: values.dates[1].format('YYYY-MM-DD'),
        motif: values.motif || null,
      };

      if (editingAbsence) {
        await updateAbsence(editingAbsence.id, data);
        message.success('Absence modifiée');
      } else {
        await createAbsence(data);
        message.success('Absence créée');
      }
      setModalOpen(false);
      fetchData();
    } catch (err) {
      message.error('Erreur lors de la sauvegarde');
    }
  };

  const columns = [
    { title: 'Équipe', dataIndex: 'equipe_nom', key: 'equipe_nom',
      render: (text, record) => <Tag>{text}</Tag>,
      filters: [...new Set(absences.map(a => a.equipe_nom))].map(n => ({ text: n, value: n })),
      onFilter: (value, record) => record.equipe_nom === value,
    },
    { title: 'Du', dataIndex: 'date_debut', key: 'date_debut',
      render: (d) => new Date(d).toLocaleDateString('fr-FR'),
      sorter: (a, b) => new Date(a.date_debut) - new Date(b.date_debut),
    },
    { title: 'Au', dataIndex: 'date_fin', key: 'date_fin',
      render: (d) => new Date(d).toLocaleDateString('fr-FR'),
    },
    { title: 'Motif', dataIndex: 'motif', key: 'motif',
      render: (m) => m || <span style={{ opacity: 0.4 }}>-</span>,
    },
    {
      title: 'Actions', key: 'actions', width: 150,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm title="Supprimer cette absence ?" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Gestion des absences</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}
          style={{ background: '#059669', borderColor: '#059669' }}>
          Ajouter
        </Button>
      </div>

      <Card>
        <Table
          dataSource={absences}
          columns={columns}
          loading={loading}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 20 }}
        />
      </Card>

      <Modal
        title={editingAbsence ? 'Modifier absence' : 'Nouvelle absence'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        okText="Enregistrer"
        cancelText="Annuler"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="equipe_id" label="Équipe" rules={[{ required: true, message: 'Sélectionnez une équipe' }]}>
            <Select
              showSearch
              placeholder="Sélectionnez une équipe"
              optionFilterProp="label"
              options={teams.filter(t => !t.deleted).map(t => ({ value: t.id, label: t.nom }))}
            />
          </Form.Item>
          <Form.Item name="dates" label="Période" rules={[{ required: true, message: 'Sélectionnez les dates' }]}>
            <RangePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="motif" label="Motif">
            <Input placeholder="Optionnel" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AbsencePage;
